import { useState, useCallback } from 'react';
import weighingService from '@/services/weighing';
import qualityAnalysisService from '@/services/qualityAnalysis';
import { useWeighing } from '@/contexts/WeighingContext';

export interface WeighingWorkflowState {
  currentStep: number;
  weighingId?: string;
  weighingCode?: string;
  tenantId?: string;
  siteId?: string;
  grossWeight?: number;
  tareWeight?: number;
  netWeight?: number;
  analysisData?: any;
  isCompleted: boolean;
  isArchived: boolean;
  
  // Nouveau : Type de processus de pesée
  weighingProcess: 'standard' | 'inverted'; // 'standard' = Processus 1, 'inverted' = Processus 2
}

export interface WeighingFormData {
  // Type de pesée
  typePesee: string;
  produit: string;
  conditionnement: string;
  
  // Informations véhicule
  vehicule: string;
  remorque: string;
  transporteur: string;
  chauffeur: string;
  convoyeur?: string;
  
  // Origine
  provenance: string;
  connaissement: string;
  
  // Destination
  destination: string;
  magasin: string;
  numeroLot: string;
  
  // Client/Fournisseur/Exportateur
  client: string;
  fournisseur: string;
  exportateur: string;
  numeroTransfert: string;
  sticker: string;
  
  // Analyse qualité
  nbSacsDeclares: number;
  poidsDeclares: number;
  nbSacsSains: number;
  nbSacsRecond: number;
  nbSacsHumides: number;
  nbSacsDechires: number;
  nbSacsRejetes: number;
  nbSacsAcceptes: number;
  poidsAcceptes: number;
  refractionHum: number;
  refractionQlt: number;
  observations: string;
}

const useWeighingWorkflow = (_pontBasculeId: string = '1') => {
  const { setCurrentWeighing, updateWeighing } = useWeighing();
  
  const [workflowState, setWorkflowState] = useState<WeighingWorkflowState>({
    currentStep: 1,
    isCompleted: false,
    isArchived: false,
    weighingProcess: 'standard' // Processus par défaut
  });

  const [formData, setFormData] = useState<WeighingFormData>({
    typePesee: 'reception',
    produit: '', // Pas de produit par défaut
    conditionnement: '', // Conditionnement par défaut (vide)
    vehicule: '', // Pas de véhicule par défaut
    remorque: '',
    transporteur: '',
    chauffeur: '', // Pas de chauffeur par défaut
    convoyeur: '',
    provenance: '',
    connaissement: '',
    destination: '',
    magasin: '',
    numeroLot: '',
    client: '',
    fournisseur: '', // Pas de fournisseur par défaut
    exportateur: '',
    numeroTransfert: '',
    sticker: '',
    nbSacsDeclares: 0,
    poidsDeclares: 0,
    nbSacsSains: 0,
    nbSacsRecond: 0,
    nbSacsHumides: 0,
    nbSacsDechires: 0,
    nbSacsRejetes: 0,
    nbSacsAcceptes: 0,
    poidsAcceptes: 0,
    refractionHum: 0,
    refractionQlt: 0,
    observations: ''
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Étape 1: Créer ou mettre à jour la pesée avec le poids brut
  const saveGrossWeight = useCallback(async (weight: number) => {
    setIsLoading(true);
    setError(null);
    
    // Valider le poids reçu
    if (typeof weight !== 'number' || isNaN(weight) || weight <= 0) {
      setError('Poids invalide reçu');
      setIsLoading(false);
      return false;
    }
    
    // Valider qu'un produit est sélectionné
    if (!formData.produit || formData.produit === '') {
      setError('Veuillez sélectionner un produit avant d\'enregistrer le poids brut');
      setIsLoading(false);
      return false;
    }
    
    // Vérifier que le poids respecte la limite du modèle (max_digits=10, decimal_places=2)
    if (weight > 99999999.99) {
      setError('Poids trop élevé (maximum: 99,999,999.99 kg)');
      setIsLoading(false);
      return false;
    }
    
    try {
      if (workflowState.weighingId) {
        // Mettre à jour la pesée existante
        await weighingService.updatePesée(parseInt(workflowState.weighingId), {
          // Mise à jour du poids brut
        } as any);
      } else {
        // Vérifier que les champs obligatoires sont présents
        if (!formData.produit) {
          setError('Le produit est obligatoire pour créer une pesée');
          setIsLoading(false);
          return null;
        }

        // À ce stade, formData.produit est garanti d'exister grâce à la validation ci-dessus
        const produitId = parseInt(formData.produit);
        if (isNaN(produitId)) {
          setError('ID de produit invalide');
          setIsLoading(false);
          return null;
        }

        // Créer une nouvelle pesée
        const newWeighing = await weighingService.createPesée({
          type_pesee: formData.typePesee,
          produit: produitId,
          vehicule: formData.vehicule ? parseInt(formData.vehicule) : undefined,
          remorque: formData.remorque && formData.remorque !== '' ? parseInt(formData.remorque) : undefined,
          transporteur: formData.transporteur && formData.transporteur !== '' ? parseInt(formData.transporteur) : undefined,
          chauffeur: formData.chauffeur ? parseInt(formData.chauffeur) : undefined,
          convoyeur: formData.convoyeur && formData.convoyeur !== '' ? parseInt(formData.convoyeur) : undefined,
          provenance: formData.provenance,
          connaissement: formData.connaissement,
          destination: formData.destination,
          magasin: formData.magasin && formData.magasin !== '' ? parseInt(formData.magasin) : undefined,
          numero_lot: formData.numeroLot,
          client: formData.client && formData.client !== '' ? parseInt(formData.client) : undefined,
          fournisseur: formData.fournisseur ? parseInt(formData.fournisseur) : undefined,
          exportateur: formData.exportateur && formData.exportateur !== '' ? parseInt(formData.exportateur) : undefined,
          numero_transfert: formData.numeroTransfert,
          sticker: formData.sticker,
          // Champs d'analyse qualité
          nb_sacs_declares: formData.nbSacsDeclares || 0,
          poids_declares: formData.poidsDeclares || 0,
          poids_brut: weight,
          statut: 'BRUT_ENREGISTRE'
        });

        // Générer un code unique
        const weighingCode = generateWeighingCode();
        
        // Extraire l'ID et les détails de la réponse de l'API
        const weighingId = (newWeighing as any).data?.id || (newWeighing as any).id || '';
        const weighingData = (newWeighing as any).data || newWeighing;
        
        setWorkflowState(prev => ({
          ...prev,
          weighingId: weighingId?.toString(),
          weighingCode,
          grossWeight: weight,
          tenantId: '1', // TODO: Récupérer depuis le contexte
          siteId: '1' // TODO: Récupérer depuis le contexte
        }));

        // Recharger la pesée avec toutes les relations pour le contexte
        try {
          const fullWeighingResponse = await weighingService.getPesée(parseInt(weighingId));
          if (fullWeighingResponse.success && fullWeighingResponse.data) {
            setCurrentWeighing(fullWeighingResponse.data);
          } else {
            // Fallback sur les données de base si le rechargement échoue
            setCurrentWeighing(weighingData);
          }
        } catch (error) {
          console.error('❌ Erreur lors du rechargement de la pesée complète:', error);
          // Fallback sur les données de base
          setCurrentWeighing(weighingData);
        }

        // Retourner les détails de la pesée créée
        return weighingData;
      }

      setWorkflowState(prev => ({
        ...prev,
        grossWeight: weight
      }));

      return true;
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Erreur lors de l\'enregistrement du poids brut';
      setError(errorMessage);
      console.error('Erreur saveGrossWeight:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [workflowState.weighingId, formData]);

  // Étape 2: Enregistrer l'analyse qualité
  const saveQualityAnalysis = useCallback(async (analysisData: any) => {
    if (!workflowState.weighingId) {
      setError('Aucune pesée active');
      return false;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      // Créer l'analyse qualité via l'API dédiée
      const response = await qualityAnalysisService.createQualityAnalysisForPesee(
        parseInt(workflowState.weighingId),
        {
          moisture: analysisData.moisture,
          proteins: analysisData.proteins,
          hectoliter_weight: analysisData.hectoliter_weight,
          impurities: analysisData.impurities,
          broken_grains: analysisData.broken_grains,
          germinated_grains: analysisData.germinated_grains,
          quality_decision: analysisData.quality_decision || 'ACCEPTE',
          observations: analysisData.observations
        }
      );

      if (response.success) {
        setWorkflowState(prev => ({
          ...prev,
          analysisData: response.data
        }));

        // Recharger la pesée complète avec toutes les relations après l'analyse qualité
        try {
          const fullWeighingResponse = await weighingService.getPesée(parseInt(workflowState.weighingId));
          if (fullWeighingResponse.success && fullWeighingResponse.data) {
            setCurrentWeighing(fullWeighingResponse.data);
          }
        } catch (error) {
          console.error('❌ Erreur lors du rechargement de la pesée après analyse qualité:', error);
        }

        return true;
      } else {
        setError(response.message);
        return false;
      }
    } catch (err) {
      setError('Erreur lors de l\'enregistrement de l\'analyse');
      console.error('Erreur saveQualityAnalysis:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [workflowState.weighingId]);

  // Étape 3: Enregistrer le poids tare et calculer le poids net
  const saveTareWeight = useCallback(async (weight: number) => {
    if (!workflowState.weighingId || !workflowState.grossWeight) {
      setError('Poids brut manquant');
      return false;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const netWeight = workflowState.grossWeight - weight;
      
     
      
      await weighingService.updatePesée(parseInt(workflowState.weighingId), {
        poids_tare: weight,
        poids_net: netWeight,
        statut: 'TARE_ENREGISTREE'
      });

      setWorkflowState(prev => ({
        ...prev,
        tareWeight: weight,
        netWeight,
        isCompleted: true
      }));

      // Recharger la pesée complète avec toutes les relations
      try {
        const fullWeighingResponse = await weighingService.getPesée(parseInt(workflowState.weighingId));
        if (fullWeighingResponse.success && fullWeighingResponse.data) {
          setCurrentWeighing(fullWeighingResponse.data);
        } else {
          // Fallback sur la mise à jour partielle
          updateWeighing({
            poids_tare: weight,
            poids_net: netWeight,
            statut: 'TARE_ENREGISTREE'
          });
        }
      } catch (error) {
        console.error('❌ Erreur lors du rechargement de la pesée complète:', error);
        // Fallback sur la mise à jour partielle
        updateWeighing({
          poids_tare: weight,
          poids_net: netWeight,
          statut: 'TARE_ENREGISTREE'
        });
      }

      return true;
    } catch (err) {
      setError('Erreur lors de l\'enregistrement du poids tare');
      console.error('Erreur saveTareWeight:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [workflowState.weighingId, workflowState.grossWeight]);

  // Archiver la pesée
  const archiveWeighing = useCallback(async (shouldArchive: boolean) => {
    if (!workflowState.weighingId) {
      setError('Aucune pesée active');
      return false;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      if (shouldArchive) {
        await weighingService.updatePesée(parseInt(workflowState.weighingId), {
          // Archivage de la pesée
        } as any);

        setWorkflowState(prev => ({
          ...prev,
          isArchived: true
        }));
      }

      return true;
    } catch (err) {
      setError('Erreur lors de l\'archivage');
      console.error('Erreur archiveWeighing:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [workflowState.weighingId]);

  // Navigation entre les étapes
  const goToStep = useCallback((step: number) => {
    if (step >= 1 && step <= 4) {
      setWorkflowState(prev => ({
        ...prev,
        currentStep: step
      }));
    }
  }, []);

  // Fonction pour vérifier si l'analyse qualité est requise
  const isQualityAnalysisRequired = useCallback(() => {
    // Si le produit n'exige pas d'analyse qualité, on peut sauter l'étape 2
    // Cette logique sera basée sur le champ qualite_requise du produit
    // Pour l'instant, on retourne true par défaut
    return true;
  }, [formData.produit]);

  // Fonction pour déterminer le processus de pesée
  const determineWeighingProcess = useCallback((_typePesee: string, _produitType?: string) => {
    // Logique pour déterminer le processus selon le type de pesée et le produit
   
    // Pour l'instant, utiliser le processus standard par défaut
    // On pourrait ajouter une logique basée sur le type de pesée ou le produit
    return 'standard';
  }, []);

  // Fonction pour obtenir l'ordre des étapes selon le processus
  const getStepOrder = useCallback((process: 'standard' | 'inverted') => {
    if (process === 'inverted') {
      return {
        1: 'tare',      // Étape 1 : Pesée tare (vide)
        2: 'quality',   // Étape 2 : Analyse qualité (optionnelle)
        3: 'gross',     // Étape 3 : Pesée brute (chargé)
        4: 'ticket'     // Étape 4 : Ticket final
      };
    } else {
      return {
        1: 'gross',     // Étape 1 : Pesée brute (chargé)
        2: 'quality',   // Étape 2 : Analyse qualité (optionnelle)
        3: 'tare',      // Étape 3 : Pesée tare (vide)
        4: 'ticket'     // Étape 4 : Ticket final
      };
    }
  }, []);

  // Fonction pour obtenir le libellé de l'étape selon le processus
  const getStepLabel = useCallback((stepNumber: number, process: 'standard' | 'inverted') => {
    const stepOrder = getStepOrder(process);
    const stepType = stepOrder[stepNumber as keyof typeof stepOrder];
    
    switch (stepType) {
      case 'gross':
        return 'Pesée brute (chargé)';
      case 'tare':
        return 'Pesée tare (vide)';
      case 'quality':
        return 'Analyse qualité';
      case 'ticket':
        return 'Ticket final';
      default:
        return 'Étape inconnue';
    }
  }, [getStepOrder]);

  // Fonction pour obtenir la description de l'étape
  const getStepDescription = useCallback((stepNumber: number, process: 'standard' | 'inverted') => {
    const stepOrder = getStepOrder(process);
    const stepType = stepOrder[stepNumber as keyof typeof stepOrder];
    
    switch (stepType) {
      case 'gross':
        return 'Capture du poids brut (véhicule chargé)';
      case 'tare':
        return 'Capture du poids tare (véhicule vide)';
      case 'quality':
        return 'Saisie des paramètres de qualité';
      case 'ticket':
        return 'Affichage et archivage du ticket';
      default:
        return 'Description inconnue';
    }
  }, [getStepOrder]);

  // Fonction pour vérifier si l'étape actuelle est une pesée
  const isWeighingStep = useCallback((stepNumber: number, process: 'standard' | 'inverted') => {
    const stepOrder = getStepOrder(process);
    const stepType = stepOrder[stepNumber as keyof typeof stepOrder];
    return stepType === 'gross' || stepType === 'tare';
  }, [getStepOrder]);

  // Fonction pour obtenir le type de pesée de l'étape actuelle
  const getCurrentWeighingType = useCallback((stepNumber: number, process: 'standard' | 'inverted') => {
    const stepOrder = getStepOrder(process);
    const stepType = stepOrder[stepNumber as keyof typeof stepOrder];
    return stepType === 'gross' ? 'gross' : stepType === 'tare' ? 'tare' : null;
  }, [getStepOrder]);

  // Fonction pour obtenir les instructions pour l'utilisateur
  const getStepInstructions = useCallback((stepNumber: number, process: 'standard' | 'inverted') => {
    const stepOrder = getStepOrder(process);
    const stepType = stepOrder[stepNumber as keyof typeof stepOrder];
    
    switch (stepType) {
      case 'gross':
        return process === 'inverted' 
          ? 'Chargez le véhicule et capturez le poids brut'
          : 'Le véhicule est chargé, capturez le poids brut';
      case 'tare':
        return process === 'inverted'
          ? 'Le véhicule est vide, capturez le poids tare'
          : 'Videz le véhicule et capturez le poids tare';
      case 'quality':
        return 'Effectuez l\'analyse qualité si nécessaire';
      case 'ticket':
        return 'Générez le ticket final';
      default:
        return 'Instructions non définies';
    }
  }, [getStepOrder]);

  // Fonction pour initialiser le processus de pesée
  const initializeWeighingProcess = useCallback((process: 'standard' | 'inverted' = 'standard') => {
    
    setWorkflowState(prev => ({
      ...prev,
      weighingProcess: process,
      currentStep: 1
    }));
  }, []);

  // Fonction pour changer de processus (si nécessaire)
  const switchWeighingProcess = useCallback((newProcess: 'standard' | 'inverted') => {
    setWorkflowState(prev => ({
      ...prev,
      weighingProcess: newProcess,
      currentStep: 1, // Redémarrer depuis le début
      grossWeight: undefined,
      tareWeight: undefined,
      netWeight: undefined
    }));
  }, []);

  const nextStep = useCallback(() => {
    if (workflowState.currentStep < 4) {
      let nextStepNumber = workflowState.currentStep + 1;
      
      // Si on est à l'étape 1 et que l'analyse qualité n'est pas requise, sauter l'étape 2
      if (workflowState.currentStep === 1 && !isQualityAnalysisRequired()) {
        nextStepNumber = 3; // Passer directement à l'étape 3
      }
      
      setWorkflowState(prev => ({
        ...prev,
        currentStep: nextStepNumber
      }));
    }
  }, [workflowState.currentStep, isQualityAnalysisRequired]);

  const previousStep = useCallback(() => {
    if (workflowState.currentStep > 1) {
      let prevStepNumber = workflowState.currentStep - 1;
      
      // Si on est à l'étape 3 et que l'analyse qualité n'est pas requise, retourner à l'étape 1
      if (workflowState.currentStep === 3 && !isQualityAnalysisRequired()) {
        prevStepNumber = 1; // Retourner directement à l'étape 1
      }
      
      setWorkflowState(prev => ({
        ...prev,
        currentStep: prevStepNumber
      }));
    }
  }, [workflowState.currentStep, isQualityAnalysisRequired]);

  // Mise à jour des données du formulaire
  const updateFormData = useCallback((field: keyof WeighingFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  // Réinitialiser le workflow
  const resetWorkflow = useCallback(() => {
    setWorkflowState({
      currentStep: 1,
      isCompleted: false,
      isArchived: false,
      weighingProcess: 'standard'
    });
    setFormData({
      typePesee: 'reception',
      produit: '',
      conditionnement: '',
      vehicule: '',
      remorque: '',
      transporteur: '',
      chauffeur: '',
      provenance: '',
      connaissement: '',
      destination: '',
      magasin: '',
      numeroLot: '',
      client: '',
      fournisseur: '',
      exportateur: '',
      numeroTransfert: '',
      sticker: '',
      nbSacsDeclares: 0,
      poidsDeclares: 0,
      nbSacsSains: 0,
      nbSacsRecond: 0,
      nbSacsHumides: 0,
      nbSacsDechires: 0,
      nbSacsRejetes: 0,
      nbSacsAcceptes: 0,
      poidsAcceptes: 0,
      refractionHum: 0,
      refractionQlt: 0,
      observations: ''
    });
    setError(null);
  }, []);

  // Générer un code de pesée unique
  const generateWeighingCode = () => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substr(2, 5);
    return `PES-${timestamp}-${random}`.toUpperCase();
  };

  // Fonction pour recharger les données complètes de la pesée actuelle
  const refreshCurrentWeighing = useCallback(async () => {
    if (!workflowState.weighingId) {
      return false;
    }

    try {
      const fullWeighingResponse = await weighingService.getPesée(parseInt(workflowState.weighingId));
      if (fullWeighingResponse.success && fullWeighingResponse.data) {
        setCurrentWeighing(fullWeighingResponse.data);
        return true;
      } else {
        console.error('❌ Échec du rechargement de la pesée:', fullWeighingResponse.message);
        return false;
      }
    } catch (error) {
      console.error('❌ Erreur lors du rechargement manuel de la pesée:', error);
      return false;
    }
  }, [workflowState.weighingId, setCurrentWeighing]);

  // Vérifier si on peut passer à l'étape suivante
  const canProceedToNextStep = useCallback((step: number) => {
    switch (step) {
      case 1:
        return !!workflowState.grossWeight && !!formData.vehicule && !!formData.produit;
      case 2:
        return !!workflowState.analysisData;
      case 3:
        return !!workflowState.tareWeight;
      default:
        return false;
    }
  }, [workflowState, formData]);

  return {
    // État
    workflowState,
    formData,
    isLoading,
    error,
    
    // Actions
    saveGrossWeight,
    saveQualityAnalysis,
    saveTareWeight,
    archiveWeighing,
    
    // Navigation
    goToStep,
    nextStep,
    previousStep,
    
    // Utilitaires
    updateFormData,
    resetWorkflow,
    canProceedToNextStep,
    refreshCurrentWeighing,
    isQualityAnalysisRequired,
    
    // Nouvelles fonctions pour les processus
    getStepOrder,
    getStepLabel,
    getStepDescription,
    isWeighingStep,
    getCurrentWeighingType,
    getStepInstructions,
    initializeWeighingProcess,
    switchWeighingProcess,
    determineWeighingProcess,
    
    // État du processus
    weighingProcess: workflowState.weighingProcess
  };
};

export default useWeighingWorkflow;
